import React from 'react';
import { ResponsivePie } from '@nivo/pie';
import { formatYAxisValue, formatHorizontalBarLabel } from '../../Components/DrillDown/formatters';
import { DonutChartSkeleton } from '../../Components/Skeletons/ChartSkeletons';
import {
  Card,
  CardContent,
  Typography,
  Box,
  useTheme
} from '@mui/material';
import { NcmDrillDown } from '../../Components/DrillDown';
import GenericModal from '../../Components/GenericModal';
import { DrillDownNcmData } from './types';

type PieChartData = {
  id: string;
  value: number;
};

type Props = {
  data: PieChartData[];
  title: string;
  loading?: boolean;
  spedFiscalId?: number;
};

const DonutChartPorNcm: React.FC<Props> = ({ data, title, loading = false, spedFiscalId }) => {
  const theme = useTheme();
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [selectedNcm, setSelectedNcm] = React.useState<string | null>(null);
  const [drillDownData, setDrillDownData] = React.useState<DrillDownNcmData | null>(null);
  const [loadingDrillDown, setLoadingDrillDown] = React.useState(false);

  // Processar dados para incluir apenas TOP 10 e versões truncadas dos labels
  const processedData = React.useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }

    // Ordenar dados por valor em ordem decrescente
    const sortedData = [...data].sort((a, b) => b.value - a.value);

    // Pegar apenas os TOP 10
    const top10 = sortedData.slice(0, 10);

    return top10.map(item => ({
      id: formatHorizontalBarLabel(item.id, 25), // Nome truncado para display nas legendas
      value: item.value,
      originalData: item, // Manter referência aos dados originais para drill-down
    }));
  }, [data]);

  const handlePieClick = async (clickData: any) => {
    if (!spedFiscalId) return;

    // Buscar item original usando os dados processados (que contêm originalData)
    const processedItem = processedData.find(item => item.id === clickData.id);
    const pieId = processedItem?.originalData?.id || clickData.id; // ex: "84733020 - Válvulas solenoides"
    
    // Extrair NCM (primeiro elemento antes do " - ")
    const ncmMatch = pieId.match(/^(\d{8})/);
    if (!ncmMatch) {
      console.error('NCM não encontrado para:', pieId);
      return;
    }
    
    const ncm = ncmMatch[1];
    setSelectedNcm(pieId);
    setIsModalOpen(true);
    setLoadingDrillDown(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}ncm-detalhes-drill-down/${spedFiscalId}/${ncm}`
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const detailData: DrillDownNcmData = await response.json();
      
      // Verificar se os dados estão estruturados corretamente
      if (detailData && typeof detailData === 'object') {
        setDrillDownData(detailData);
      } else {
        console.error('Estrutura de dados inválida:', detailData);
      }
    } catch (error) {
      console.error('Erro ao buscar detalhes do NCM:', error);
    } finally {
      setLoadingDrillDown(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedNcm(null);
    setDrillDownData(null);
  };





  const renderDetailView = () => {
    if (!drillDownData) return null;
    
    // Adaptando dados para garantir compatibilidade de tipos
    const adaptedData = {
      ...drillDownData,
      ncm: drillDownData.ncm ? {
        ...drillDownData.ncm,
        cod_item: (drillDownData?.ncm as any)?.cod_item || 'N/A',
        valor_total_item_compra: drillDownData.ncm.valor_total_item_compra || 0,
        valor_total_item_venda: drillDownData.ncm.valor_total_item_venda || 0
      } : undefined,
      parceiros_comerciais: drillDownData.parceiros_comerciais ? {
        fornecedores: drillDownData.parceiros_comerciais.fornecedores.map(f => ({
          ...f,
          total_competencias: 0,
          competencias_ativas: ''
        })),
        clientes: drillDownData.parceiros_comerciais.clientes.map(c => ({
          ...c,
          total_competencias: 0,
          competencias_ativas: ''
        }))
      } : { fornecedores: [], clientes: [] }
    };
    
    return <NcmDrillDown data={adaptedData} />;
  };

  const renderListView = () => (
    <Box>
      <Box sx={{ height: 450, width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        {loading ? (
          <DonutChartSkeleton height={450} showTitle={false} />
        ) : data.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nenhum dado disponível.
          </Typography>
        ) : (
          <ResponsivePie
            data={processedData}
            onClick={spedFiscalId ? handlePieClick : undefined}
            valueFormat={(value) => formatYAxisValue(value)}
            margin={{ top: 40, right: 60, bottom: 40, left: 60 }}
            innerRadius={0.55}
            padAngle={1.5}
            cornerRadius={5}
            activeOuterRadiusOffset={12}
            colors={{ scheme: 'pastel1' }}
            borderWidth={1}
            borderColor={{ from: 'color', modifiers: [['darker', 0.3]] }}
            arcLinkLabelsSkipAngle={12}
            arcLinkLabelsTextColor={theme.palette.text.primary}
            arcLinkLabelsThickness={2}
            arcLinkLabelsColor={{ from: 'color' }}
            arcLabelsSkipAngle={10}
            arcLabelsTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
            tooltip={({ datum }: { datum: any }) => {
              // Usar dados originais do processedData
              const processedItem = processedData.find(item => item.id === datum.id);
              const originalId = processedItem?.originalData?.id || datum.id;
              return (
                <Box
                  sx={{
                    background: theme.palette.background.paper,

                    px: 2,
                    py: 1,
                    maxWidth: 300
                  }}
                >
                  <Typography variant="body2" fontWeight={500}>
                    {originalId}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL'
                    }).format(datum.value)}
                  </Typography>
                  {spedFiscalId && (
                    <Typography variant="caption" color="primary.main" sx={{ display: 'block', mt: 0.5 }}>
                      Clique para ver detalhes
                    </Typography>
                  )}
                </Box>
              );
            }}
            animate
            motionConfig="gentle"
          />
        )}
      </Box>
    </Box>
  );

  return (
    <>
              <Card
          sx={{
            p: 2,
            height: 500,
            overflow: 'visible',
            position: 'relative',
            zIndex: 1
          }}
        >
        <CardContent sx={{ 
          p: 0, 
          overflow: 'visible',
          position: 'relative',
          zIndex: 2
        }}>
          {!loading && (
            <Typography
              variant="subtitle1"
              align="center"
              sx={{ color: theme.palette.text.secondary, mb: 2 }}
            >
              {title}
            </Typography>
          )}

          {loading ? (
            <DonutChartSkeleton height={450} showTitle={false} />
          ) : (
            renderListView()
          )}
        </CardContent>
      </Card>

      <GenericModal
        open={isModalOpen}
        onClose={handleCloseModal}
        title="Detalhamento do NCM"
        loading={loadingDrillDown}
        showBreadcrumb={true}
        breadcrumbItems={[{ label: selectedNcm || 'NCM' }]}
        maxWidth="xl"
        height="90vh"
      >
        {renderDetailView()}
      </GenericModal>
    </>
  );
};

export default DonutChartPorNcm;
